import { useState, useEffect, useCallback, useMemo } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { projectService, type ProjectDto } from '@/services/projectService';
import { Badge } from '@/components/ui/badge';
import { Activity, Database, CloudDownload, ArrowDown01, ChartNetwork, Clock, Download, Loader, BarChart3, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { statisticService, UsageMetric, LastActivity, UsageHistoryItem, type ChatbotUsageItem } from '@/services/statisticService';
import { toast } from '@/components/ui/sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, LineChart, Line, type TooltipProps } from 'recharts';
import { calculateCredits, createEmptyCreditUsage, type CreditUsage } from '@/lib/credits';

const normalizeEventType = (eventType: string = '') => eventType.replace(/[^a-z]/gi, '').toLowerCase();

const formatMonthForDisplay = (value: string | number) => {
  const stringValue = typeof value === 'number' ? String(value) : value;
  const [year, month] = stringValue.split('-');
  if (!year || !month) return stringValue;
  return `${month}/${year.slice(2)}`;
};

const normalizeNumericValue = (value: string | number | (string | number)[] | undefined) => {
  if (Array.isArray(value)) {
    const [first] = value;
    return typeof first === 'number' ? first : Number(first);
  }

  return typeof value === 'number' ? value : Number(value);
};

const formatCreditValue = (value: string | number | (string | number)[]) => {
  const numeric = normalizeNumericValue(value);
  if (!Number.isFinite(numeric)) return '0.00';
  return numeric.toFixed(2);
};

const CreditsTooltip = ({ active, payload, label }: TooltipProps<string, string>) => {
  if (!active || !payload || payload.length === 0) return null;

  const value = payload[0]?.value ?? 0;

  return (
    <div className="rounded-md bg-slate-900/90 px-3 py-2 text-xs text-white shadow-lg">
      <div className="text-[11px] uppercase tracking-wide text-slate-300">
        {formatMonthForDisplay(label ?? '')}
      </div>
      <div className="text-sm font-semibold">
        {formatCreditValue(value)} credits
      </div>
    </div>
  );
};

const CREDIT_EVENT_TYPE_MAP: Record<string, keyof CreditUsage> = {
  prompttokens: 'promptTokens',
  prompttoken: 'promptTokens',
  completiontokens: 'completionTokens',
  completiontoken: 'completionTokens',
  textintokens: 'textIn',
  textin: 'textIn',
  textouttokens: 'textOut',
  textout: 'textOut',
  audiointokens: 'audioIn',
  audioin: 'audioIn',
  audioouttokens: 'audioOut',
  audioout: 'audioOut',
};

const UsageStats = () => {
  const [timeRange, setTimeRange] = useState('6m');
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('TEAM');
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usageMetrics, setUsageMetrics] = useState<UsageMetric[]>([]);
  const [lastActivity, setLastActivity] = useState<LastActivity | null>(null);
  const [usageHistory, setUsageHistory] = useState<UsageHistoryItem[]>([]);
  const [chatbotUsage, setChatbotUsage] = useState<ChatbotUsageItem[]>([]);
  const [subscriptionName, setSubscriptionName] = useState<string>('');
  
  
  // Fetch overall usage metrics and last activity (once on mount)
  const fetchOverallStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await statisticService.getUsageStats({ projectId: selectedProjectId === 'TEAM' ? undefined : selectedProjectId });
      setUsageMetrics(data.usageMetrics);
      setLastActivity(data.lastActivity);
      setChatbotUsage(data.chatbotUsage ?? []);
      setSubscriptionName(data.subscriptionName || '');
    } catch (err) {
      console.error('Error fetching overall stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch usage statistics');
      toast.error('Failed to load usage statistics');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  // Fetch usage history based on time range
  const fetchUsageHistory = useCallback(async (range: string) => {
    try {
      setHistoryLoading(true);
      const now = new Date();
      const monthsToShow = range === '12m' ? 12 : 6;
      const start = new Date(now.getFullYear(), now.getMonth() - (monthsToShow - 1), 1);
      const startDate = start.toISOString();
      const endDate = now.toISOString();
      const history = await statisticService.getUsageHistory(startDate, endDate, selectedProjectId === 'TEAM' ? undefined : selectedProjectId);
      setUsageHistory(history);
    } catch (err) {
      console.error('Error fetching usage history:', err);
      toast.error('Failed to load usage history');
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedProjectId]);

  // Load overall data on component mount
  useEffect(() => {
    (async () => {
      try {
        const list = await projectService.list();
        setProjects(list);
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    fetchOverallStats();
  }, [fetchOverallStats]);

  // Load usage history when timeRange changes
  useEffect(() => {
    if (usageMetrics.length > 0) { // Only fetch history after overall stats are loaded
      fetchUsageHistory(timeRange);
    }
  }, [timeRange, usageMetrics.length, fetchUsageHistory]);

  // Handle time range change
  const handleTimeRangeChange = (newRange: string) => {
    setTimeRange(newRange);
  };

  // Prepare chart data for the selected type
  const getChartData = () => {
    const monthlyUsage = usageHistory.reduce(
      (acc, item) => {
        const date = new Date(item.date);
        if (Number.isNaN(date.getTime())) {
          return acc;
        }

        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        if (!acc[monthKey]) {
          acc[monthKey] = {
            usage: createEmptyCreditUsage(),
            precomputedCredits: 0,
          };
        }

        const normalizedType = normalizeEventType(item.eventType);
        const usageKey = CREDIT_EVENT_TYPE_MAP[normalizedType];
        const count = typeof item.count === 'number' && Number.isFinite(item.count) ? item.count : 0;

        if (usageKey) {
          acc[monthKey].usage[usageKey] += count;
        } else if (normalizedType === 'credits') {
          acc[monthKey].precomputedCredits += count;
        }

        return acc;
      },
      {} as Record<string, { usage: CreditUsage; precomputedCredits: number }>
    );

    // Generate all months in the time range
    const allMonths: { month: string; credits: number }[] = [];
    const now = new Date();
    const monthsToShow = timeRange === '12m' ? 12 : 6;
    
    for (let i = monthsToShow - 1; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      const monthUsage = monthlyUsage[monthKey];
      const hasDetailedUsage = monthUsage ? Object.values(monthUsage.usage).some((value) => value > 0) : false;
      const calculatedCredits = hasDetailedUsage ? calculateCredits(monthUsage?.usage) : 0;
      const credits = hasDetailedUsage
        ? calculatedCredits
        : monthUsage?.precomputedCredits || 0;

      allMonths.push({
        month: monthKey,
        credits,
      });
    }

    return allMonths;
  };

  const chatbotDailyData = useMemo(() => {
    if (!chatbotUsage || chatbotUsage.length === 0) return [] as { date: string; credits: number }[];

    const totals = chatbotUsage.reduce((acc, entry) => {
      const parsedDate = new Date(entry.date);
      if (Number.isNaN(parsedDate.getTime())) {
        return acc;
      }
      const key = parsedDate.toISOString().slice(0, 10);
      const credits = typeof entry.credits === 'number' && Number.isFinite(entry.credits)
        ? entry.credits
        : Number(entry.credits) || 0;
      acc.set(key, (acc.get(key) ?? 0) + credits);
      return acc;
    }, new Map<string, number>());

    return Array.from(totals.entries())
      .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
      .map(([date, credits]) => ({ date, credits }));
  }, [chatbotUsage]);

  const formatDayForDisplay = (value: string | number) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return String(value);
    }
    return parsed.toLocaleDateString();
  };

  // Get icon for metric
  const getMetricIcon = (metricName: string) => {
    switch (metricName.toLowerCase()) {
      case 'credits':
        return <CreditCard size={18} className="text-green-500" />;
      case 'memory reranking calls':
        return <ArrowDown01 size={18} className="text-blue-500" />;
      case 'total graph memory nodes':
      case 'graph memories':
        return <ChartNetwork size={18} className="text-recallio-green" />;
      case 'total memories':
      case 'memories':
        return <Database size={18} className="text-recallio-yellow" />;
      case 'memory retrieval calls':
      case 'memory sources':
        return <CloudDownload size={18} className="text-purple-500" />;
      default:
        return <Activity size={18} className="text-gray-500" />;
    }
  };

  // Get badge color for event type
  const getBadgeColor = (eventType: string) => {
    switch (eventType.toLowerCase()) {
      case 'memory write':
      case 'memorywrite':
        return 'border-recallio-green/20 text-recallio-green bg-recallio-green/10';
      case 'memory recall':
      case 'memoryrecall':
        return 'border-blue-500/20 text-blue-500 bg-blue-500/10';
      case 'memory delete':
      case 'memorydelete':
        return 'border-red-500/20 text-red-500 bg-red-500/10';
      default:
        return 'border-gray-500/20 text-gray-500 bg-gray-500/10';
    }
  };

  if (loading) {
    return (
      <PageLayout 
        title="Usage Statistics" 
        description="Monitor your API usage, memory consumption, and user activity"
        badge={subscriptionName && (
          <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
            {subscriptionName}
          </Badge>
        )}
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading usage statistics...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || usageMetrics.length === 0 || !lastActivity) {
    return (
      <PageLayout 
        title="Usage Statistics" 
        description="Monitor your API usage, memory consumption, and user activity"
        badge={subscriptionName && (
          <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
            {subscriptionName}
          </Badge>
        )}
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error || 'Failed to load data'}</p>
            <Button onClick={() => fetchOverallStats()}>
              Try Again
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Usage Statistics" 
      description="Monitor your API usage, memory consumption, and user activity"
      badge={subscriptionName && (
        <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
          {subscriptionName}
        </Badge>
      )}
    >
      <div className="space-y-6 w-full">
        {/* Project Filter */}
        <div className="flex items-center gap-3">
          <div className="min-w-[260px]">
            <Select value={selectedProjectId} onValueChange={(val) => setSelectedProjectId(val)}>
              <SelectTrigger>
                <SelectValue placeholder="Team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TEAM">Team</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {/* Usage Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {usageMetrics.map((metric, index) => (
            <Card key={index} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getMetricIcon(metric.name)}
                    <CardTitle className="text-sm font-medium">{metric.name}</CardTitle>
                  </div>
                
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-baseline">
                    <span className="text-2xl font-bold">{metric.used.toLocaleString()}</span>
                    <span className="text-sm text-muted-foreground">
                      of {metric.limit==-1?"∞":metric.limit.toLocaleString()} {metric.unit}
                    </span>
                  </div>
                  <Progress value={metric.percentage} className="h-2" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{metric.limit==-1?"":(metric.percentage.toFixed(1)+"% used")}</span>
                    <span>{metric.limit==-1?"":("Per "+metric.term)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Last Activity */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Last Activity</CardTitle>
            </div>
            <CardDescription>
              {lastActivity.timeAgo === 'Never' 
                ? 'No recent activity found' 
                : `Your most recent API activity was ${lastActivity.timeAgo}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`h-2 w-2 rounded-full ${
                  lastActivity.timeAgo === 'Never' ? 'bg-gray-500' : 'bg-green-500'
                }`}></div>
                <div>
                  <p className="font-medium">{lastActivity.eventType}</p>
                  <p className="text-sm text-muted-foreground">
                    {lastActivity.projectId && lastActivity.userId ? (
                      <>Project: {lastActivity.projectId} • User: {lastActivity.userId}</>
                    ) : (
                      'No activity details available'
                    )}
                  </p>
                </div>
              </div>
              <span className="text-sm text-muted-foreground">{lastActivity.timeAgo}</span>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Usage History */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <div>
                <CardTitle>Monthly Usage Trends</CardTitle>
                <CardDescription>Track your API usage patterns over time</CardDescription>
              </div>
              <div className="flex gap-3">
                <Select value={timeRange} onValueChange={handleTimeRangeChange}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Select time range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6m">Last 6 months</SelectItem>
                    <SelectItem value="12m">Last 12 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <Loader className="h-6 w-6 animate-spin mx-auto mb-2" />
                  <p className="text-muted-foreground">Loading usage data...</p>
                </div>
              </div>
            ) : usageHistory.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No usage data found for the selected time period.
              </div>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={getChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      tickFormatter={formatMonthForDisplay}
                    />
                    <YAxis 
                      tickFormatter={formatCreditValue}
                      allowDecimals
                    />
                    <RechartsTooltip
                      cursor={{ fill: 'hsl(var(--muted))', opacity: 0.18 }}
                      content={<CreditsTooltip />}
                    />
                    <Bar 
                      dataKey="credits" 
                      fill="#3b82f6" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Chatbot Credit Usage Trend */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-1">
              <CardTitle>Daily Chatbot Credit Usage Trend</CardTitle>
              <CardDescription>Monitor chatbot credit consumption aggregated by day</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {chatbotDailyData.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                No chatbot usage data available for the selected scope.
              </div>
            ) : (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chatbotDailyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDayForDisplay}
                    />
                    <YAxis
                      tickFormatter={formatCreditValue}
                      allowDecimals
                    />
                    <RechartsTooltip
                      cursor={{ stroke: 'hsl(var(--muted))', strokeWidth: 1, opacity: 0.25 }}
                      content={<CreditsTooltip />}
                    />
                    <Line
                      type="monotone"
                      dataKey="credits"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default UsageStats;
